/**
 * RFQ IDP target schema — extraction hints for the existing Document Intelligence pipeline.
 * Keep descriptions precise to avoid ambiguous mappings (IMO vs IMPA, deadline vs delivery date).
 */
export const RFQ_IDP_TARGET_SCHEMA: Record<string, string> = {
  // Core identity / commercial
  revisionNo: 'RFQ revision number or revision code (e.g. 00, Rev A), not the RFQ number itself',
  currency: 'Quote currency code or currency name (e.g. USD, EUR, AED)',
  quoteDeadline:
    'Quotation response / quote submission deadline date only — NOT requested delivery date, NOT lead date, NOT expected delivery date',
  quoteValidityDays:
    'Quotation validity period in days (e.g. 15, 30, 60) — NOT delivery lead time',
  urgencyLevel: 'RFQ urgency or priority at header level (e.g. Normal, High, Critical)',
  requisitionRef: 'Customer requisition / purchase requisition / PMS job reference number',

  // Vessel (not item identifiers)
  vesselName: 'Vessel name or ship name from the RFQ header — NOT a part description',
  imoNo:
    'Vessel IMO number (7-digit ship identification) — NOT IMPA/MSF part codes, NOT part numbers',

  equipmentTags: 'Equipment tags, machinery tags, or equipment identifiers listed on the RFQ',
  category: 'Product / spare-parts category (e.g. Engine Parts, Pumps)',
  subCategory: 'Product subcategory under the category when explicitly stated',
  brand: 'Equipment manufacturer / brand / make',
  model: 'Equipment model designation (header-level equipment model) — NOT item model/serial',
  department: 'Requesting department (e.g. engine, deck, cabin, operations, safety)',
  estimatedTotalCost: 'Estimated total cost or budget amount for the RFQ when stated',
  remarks: 'Header-level RFQ remarks / general notes — NOT line-item remarks',

  // Contact
  contactName: 'Buyer / requester contact person name',
  contactEmail: 'Buyer / requester contact email address',
  contactPhone: 'Buyer / requester contact phone number',
  contactDepartment: 'Contact person department (e.g. Procurement, Technical)',

  // Delivery / port / warehouse
  deliveryRequiredAt: 'Delivery location type when stated (port or warehouse)',
  deliveryType: 'Delivery type when stated (e.g. berth, anchorage, warehouse)',
  portName: 'Supply / delivery port name — NOT warehouse address',
  portCountry: 'Country of the supply / delivery port when stated',
  portAgentName: 'Port agent company name',
  portAddress: 'Port agent address when stated',
  portEtaDate:
    'Vessel / cargo ETA date at the supply port — NOT quotation deadline, NOT item expected delivery date',
  warehouseAddress1: 'Warehouse delivery address line when delivery is to warehouse',
  warehousePincode: 'Warehouse postal / ZIP code',
  warehouseCity: 'Warehouse city',
  warehouseState: 'Warehouse state / province',
  warehouseCountry: 'Warehouse country',
  leadDate:
    'Lead time or lead date when explicitly labeled as lead time/date — NOT quote deadline, NOT item expected delivery',

  // Vendors
  vendors: 'Vendor / supplier company names (comma-separated if multiple)',

  // Terms (existing RFQ_TERMS_FIELDS keys)
  creditType: 'Credit type / credit arrangement term',
  paymentTerm: 'Payment term (e.g. Net 30 Days)',
  insuranceTerm: 'Insurance responsibility term',
  taxTerm: 'Tax term (e.g. GST Extra)',
  transportTerm: 'Transport / freight term',
  deliveryTerm: 'Delivery term / delivery lead-time wording for terms section',
  packingTerm: 'Packing term for commercial terms section',
  requiredPaymentTerms: 'Required payment terms text when separate from paymentTerm',

  // Certificates / documents / packing / partial flags
  requiredCertificates:
    'Required certificates list (comma-separated), e.g. Class, OEM, COC, MTC',
  requiredDocuments:
    'Required documents list (comma-separated), e.g. Commercial Invoice, Packing List',
  packingInstructions: 'Packing instructions free text when present',
  partialDeliveryAllowed: 'Whether partial delivery is allowed (yes/no) when stated',
  partialQuoteAllowed: 'Whether partial quote is allowed (yes/no) when stated',
  splitOrderAllowed: 'Whether split order is allowed (yes/no) when stated',

  // Line items
  items:
    'Array of RFQ line items. Each item may include: itemDescription (or description), ' +
    'requiredQuantity (or qty), uom, partNo, impaNo (IMPA/MSF code — NOT vessel IMO), ' +
    'altPartNo, drawingNumber (or drawingNo), dimensions, quality, ' +
    'deliveryPriority (or priority), generalRemark (or remarks), modelSerial, positionNo, ' +
    'expectedDeliveryDate (item-level requested/expected delivery date — NOT quoteDeadline). ' +
    'Omit fields that are not present on the document; do not invent quantity, UOM, or dates.',
};
